import React from "react";
import Pitch from "../../components/Pitch";
import SecondaryButton from "../../ui/SecondaryButton";
import { IGameModel, IGameModelSimulation } from "../../types/IClubDetail";
import { ChemistryPlayerPositions, FormationData, TeamChemistry, TeamPasses } from "../../types/IOption";
import LabelText from "../../ui/LabelText";
import LabelNumber from "../../styled-components/LabelNumber";
import { getBorderColor, getBackgroundColor } from "../../types/IPerformanceVersion2";
import PositiveArrow from '../../assets/icons/PositiveArrow.png';
import NegativeArrow from '../../assets/icons/NegativeArrow.png';
import { hasPlayersFromDifferentClubs } from "../../utils/drawChemistrys";

interface PitchForPDFProps {
  selectedSimulationTab: string;
  teamId: number;
  gameModelData: IGameModel[] | IGameModelSimulation;
  possessionPlayers: ChemistryPlayerPositions[];
  positionResults: ChemistryPlayerPositions[];
  teamChemistry: TeamChemistry[];
  possessionPasses: TeamPasses[];
  formationData: FormationData[];
  selectedFormation: number;
  seasonLabel?: string;
  coachName?: string;
  activeformation?: string;
  activeArcheTypeName?: string;
  overAllswappSystemFitScore?: number;
  overallSystemFitScore?: number;
  inPlayerIds?: number[];
  outPlayerIds?: number[];
  swapHistoryLength?: number;
}

const PitchForPDF : React.FC<PitchForPDFProps> = ({
    selectedSimulationTab,
    teamId, gameModelData,
    possessionPlayers, positionResults,
    teamChemistry, possessionPasses,
    formationData, selectedFormation,
    seasonLabel,
    coachName,
    activeformation,
    activeArcheTypeName,
    overAllswappSystemFitScore,
    overallSystemFitScore,
    inPlayerIds,
    outPlayerIds,
    swapHistoryLength = 0
}) => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginTop: '10px'
      }}
    >
      {selectedSimulationTab === 'systemFit' && (
        <p style={{marginTop: "30px", width: '1055px', fontFamily: 'Aeonik, sans-serif', fontSize: '20px', fontStyle: 'normal', fontWeight: 300, color:'#7C7D79' }} className="valuegraph-toggle-text">
          System Fit: Assessing how effectively each player interacts with others. The dashed lines represent the predicted system fit, which is calculated based on the abilities of the two players in question. For example, the model may predict that a winger who is very skilled at crossing the ball will interact well with a striker who is highly effective at heading the ball.
        </p>
      )}

      <div className="performance-left-container" style={{ marginTop: "5px", width: '1055px' }}>
        {selectedSimulationTab === 'systemFit' && seasonLabel && coachName && activeformation && (
          <div className="pitch-selection-container">
            <SecondaryButton
              label={`${seasonLabel} > ${coachName} > ${activeformation} - ${activeArcheTypeName || ''}`}
            />
          </div>
        )}
        {selectedSimulationTab === 'systemFit' && (
          <div className='values-box' style={{ backgroundColor: "white", width: '98.5%', height: "auto" }}>
            <div className='values-box-content'>
              <div className='content-value'>
                <LabelText className="secondary-cta-text">System fit</LabelText>
                <div className='pitch-selection-container' style={{ padding: '0px' }}>
                  {(() => {
                    const systemFitDiff = (overAllswappSystemFitScore || 0) - (overallSystemFitScore || 0);
                    const hasInPlayers = inPlayerIds && inPlayerIds.length > 0;
                    const hasOutPlayers = outPlayerIds && outPlayerIds.length > 0;
                    return hasInPlayers && hasOutPlayers && systemFitDiff !== 0 && hasPlayersFromDifferentClubs({positionResults, teamId}) && (
                      <LabelNumber className='event-player-card-details' style={{ color: systemFitDiff > 0 ? '#00D1A8' : '#FF3864' }}>
                        <img height={16} width={16} src={systemFitDiff > 0 ? PositiveArrow : NegativeArrow} alt="negative" />
                        {`${Math.abs(systemFitDiff)}`}
                      </LabelNumber>
                    )
                  })()}
                  <div className="values-content" style={{ backgroundColor: getBackgroundColor(overAllswappSystemFitScore || overallSystemFitScore || 0), border: `1px solid ${getBorderColor(overallSystemFitScore || 0)}` }}>
                    <LabelNumber className="event-player-card-details">{(overAllswappSystemFitScore || overallSystemFitScore || 0).toFixed(2)}</LabelNumber>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div style={{ width: "100%", height: "auto" }}>
          <div className="pitch-container-for-pdf">
            {selectedSimulationTab === "gameModel" ? (
              swapHistoryLength === 0 ? (
                // Single view when no swaps
                <Pitch
                  currentView="GameModel"
                  selectedTeam={Number(teamId)}
                  gameModelData={Array.isArray(gameModelData) ? gameModelData : (gameModelData as IGameModelSimulation).inGameModel}
                  gameModelPlayers={Array.isArray(gameModelData) ? [] : (gameModelData as IGameModelSimulation).inPlayersDetail}
                  selectedMatch={0}
                  isForSimulation={true}
                  isVerticalGameModel={false}
                  setSwapHoverPlayer={() => {}}
                  formationName={formationData.find((form) => form.id === selectedFormation)?.formation}
                  handleSwapPlayer={async () => {}}
                />
              ) : (
                // Side-by-side view when there are swaps
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', height: '600px', justifyContent: 'center', marginTop: '40px' }}>
                  {/* Out Game Model (Left Side) */}
                  <div style={{ width: '800px', height: '430px' }}>
                    <Pitch
                      currentView="GameModel"
                      selectedTeam={Number(teamId)}
                      gameModelData={Array.isArray(gameModelData) ? gameModelData : (gameModelData as IGameModelSimulation).outGameModel}
                      gameModelPlayers={Array.isArray(gameModelData) ? [] : (gameModelData as IGameModelSimulation).outPlayersDetail}
                      selectedMatch={0}
                      isForSimulation={true}
                      isVerticalGameModel={true}
                      setSwapHoverPlayer={() => {}}
                      formationName={formationData.find((form) => form.id === selectedFormation)?.formation}
                      handleSwapPlayer={async () => {}}
                    />
                  </div>

                  {/* In Game Model (Right Side) */}
                  <div style={{ width: '800px', height: '430px', marginLeft: '-200px' }}>
                    <Pitch
                      currentView="GameModel"
                      selectedTeam={Number(teamId)}
                      gameModelData={Array.isArray(gameModelData) ? gameModelData : (gameModelData as IGameModelSimulation).inGameModel}
                      gameModelPlayers={Array.isArray(gameModelData) ? [] : (gameModelData as IGameModelSimulation).inPlayersDetail}
                      selectedMatch={0}
                      isForSimulation={true}
                      isVerticalGameModel={true}
                      setSwapHoverPlayer={() => {}}
                      formationName={formationData.find((form) => form.id === selectedFormation)?.formation}
                      handleSwapPlayer={async () => {}}
                    />
                  </div>
                </div>
              )
            ) : (
              // Non-GameModel views (systemFit, possessions)
              <Pitch
                currentView={
                  selectedSimulationTab === "possessions"
                    ? "passingNetwork"
                    : "systemFit"
                }
                selectedTeam={Number(teamId)}
                selectedMatch={
                  selectedSimulationTab === "possessions"
                    ? possessionPlayers?.length! > 0
                      ? 0
                      : 1
                    : 0
                }
                positionResults={
                  selectedSimulationTab === "possessions"
                    ? possessionPlayers
                    : positionResults
                }
                teamChemistry={
                  selectedSimulationTab === "possessions" ? [] : teamChemistry
                }
                passNetworkPlayers={
                  selectedSimulationTab === "possessions"
                    ? possessionPlayers
                    : undefined
                }
                passNetworkPlayerPasses={
                  selectedSimulationTab === "possessions"
                    ? possessionPasses
                    : undefined
                }
                isForSimulation={true}
                setSwapHoverPlayer={() => {}}
                formationName={formationData.find((form) => form.id === selectedFormation)?.formation}
                handleSwapPlayer={async () => {}}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchForPDF;
